// SCARA DESENATĂ DIN RAMPE — rampele trase de om, cu podestele dintre ele.
//
// A DOUA FORMĂ A ACELEIAȘI PIESE, nu un al doilea fel de scară. `arhMobilierPlan`
// desenează scara care a fost PUSĂ: o cutie, cu rampele împărțite în ea după
// regulă. Aici e scara care a fost TRASĂ, rampă cu rampă, ca în Revit — și
// atunci rampele nu mai sunt o consecință a cutiei, ci invers.
//
// Ce rămâne comun stă în `scara.ts`: câte contratrepte urcă nivelul, câte pune
// o rampă de lungimea trasă, cât iese cutia. Aici e numai desenul.
//
// Coordonatele sunt ALE PLANULUI, nu ale unei cutii centrate: o scară trasă are
// rampele unde le-a dus mâna, iar a le muta într-un cadru centrat ar însemna să
// le recalculezi la fiecare desen.

import type { ReactNode } from "react";
import { formaScarii, scaraDinRampe, type Rampa } from "./scara";

const LINIE = "var(--mob-linie, #111111)";
const FOND = "var(--mob-corp, #ffffff)";
const K = 55;
const g = (m: number) => `calc(var(--mob-gros, 1) * ${Math.max(0.6, m * K)}px)`;

type Pt = [number, number];

/** Versorul rampei și normala ei. */
function axe(r: Rampa) {
  const dx = r.la[0] - r.de[0], dy = r.la[1] - r.de[1];
  const L = Math.hypot(dx, dy) || 1;
  return { ux: dx / L, uy: dy / L, nx: -dy / L, ny: dx / L, L };
}

/** Colțurile dreptunghiului unei rampe: linia trasă, lățită în ambele părți. */
export function colturiRampei(r: Rampa, lat: number): Pt[] {
  return colturi(r, lat);
}

/** PĂRȚILE SCĂRII, ca poligoane: rampele și podestele dintre ele.
 *
 *  Scoase afară fiindcă nu mai sunt doar desen: pe ele se și APASĂ. O rampă și
 *  un podest se aleg separat, ca să li se poată spune câte trepte au și cât
 *  sunt de adânci — deci forma lor trebuie să fie una și aceeași pentru ce se
 *  vede și pentru ce se apucă (05.10.2026). */
export function partileScarii(rampe: readonly Rampa[], lat: number,
                              adancPodest?: number,
                              contururi?: readonly (readonly Pt[] | undefined)[]) {
  return {
    rampe: rampe.map((r) => colturi(r, lat)),
    // FORMA SCRISĂ BATE CEA DEDUSĂ. Un podest desenat de om — ori citit de pe
    // o planșă cu fațadă oblică — are forma lui; fără ea se deduce mai departe
    // dreptunghiul dintre rampe, deci scările de până acum nu se schimbă.
    podeste: rampe.slice(1).map((r, i) => {
      const scris = contururi?.[i];
      return scris && scris.length >= 3
        ? scris.map((q) => [q[0], q[1]] as Pt)
        : podest(rampe[i], r, lat, adancPodest);
    }),
  };
}

/** FORMELE SCRISE ale podestelor, pe indicele lor, aduse în cadrul desenului.
 *
 *  Conturul e scris în coordonatele PLANULUI, ca rampele, dar fiecare randor
 *  desenează scara în alt cadru: planșa într-unul mutat în centrul piesei,
 *  randarea 3D într-unul rotit. Aducerea se dă deci ca funcție — `adu` —, și
 *  socoteala «care contur e al cărui podest» se face o singură dată, aici,
 *  pentru toți cei trei chemători. */
export function podesteleScarii(
  sc: { podeste?: readonly { k: number; contur: readonly Pt[] }[] } | undefined,
  adu: (p: Pt) => Pt = (p) => p,
): (Pt[] | undefined)[] {
  const out: (Pt[] | undefined)[] = [];
  for (const x of sc?.podeste ?? []) {
    // ATENȚIE LA NUMELE ĂSTA. Cititorul de DWG (`arhimport/scara.py`) scrie și
    // el un câmp `podeste`, dar al lui e altceva: golurile dintre rampe, ca
    // perechi `{de, la}`. Cele două nu se întâlnesc azi — DWG-ul nu scrie
    // niciodată piese de mobilier —, dar dacă s-ar întâlni, o formă fără
    // `contur` ar trebui lăsată în pace, nu citită greșit.
    if (x.k >= 0 && Array.isArray(x.contur) && x.contur.length >= 3) {
      out[x.k] = x.contur.map((q) => adu([q[0], q[1]]));
    }
  }
  return out;
}

/** THE BOX OF A DRAWN STAIR: the plan-aligned envelope of every part the
 *  drawing shows, flights and landings, written contours included.
 *
 *  The piece's stored box is refit from it after every change, so what the
 *  editor grabs, turns and snaps is the shape the architect sees. A box of
 *  the flights alone (`scara::cutiaRampelor`) left the landing outside the
 *  dashed rectangle and read a U stair 1.25 m shorter than it is drawn. */
export function cutiaScarii(sc: {
  rampe: readonly Rampa[]; latime_m: number; podest_m?: number;
  podeste?: readonly { k: number; contur: readonly Pt[] }[];
}): { cx: number; cy: number; w: number; h: number } | null {
  if (!sc.rampe.length) return null;
  const parti = partileScarii(sc.rampe, sc.latime_m, sc.podest_m,
                              podesteleScarii(sc));
  const puncte = [...parti.rampe.flat(), ...parti.podeste.flat()];
  const xs = puncte.map((q) => q[0]), ys = puncte.map((q) => q[1]);
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  const y0 = Math.min(...ys), y1 = Math.max(...ys);
  return { cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, w: x1 - x0, h: y1 - y0 };
}

/** THE SHAPE'S TWO SIZES: its extent along the first flight (the length)
 *  and across it (the width), every part counted. On a U they are the flight
 *  plus the landing's depth, and two flights plus the eye; on a single flight
 *  they are the run and the flight's width. The frame is the first flight's
 *  because that is the way the stair starts climbing, the only direction a
 *  stair of any shape agrees on. */
export function intinderileScarii(sc: {
  rampe: readonly Rampa[]; latime_m: number; podest_m?: number;
  podeste?: readonly { k: number; contur: readonly Pt[] }[];
}): { length: number; width: number } | null {
  if (!sc.rampe.length) return null;
  const { ux, uy, nx, ny } = axe(sc.rampe[0]);
  const parti = partileScarii(sc.rampe, sc.latime_m, sc.podest_m,
                              podesteleScarii(sc));
  const puncte = [...parti.rampe.flat(), ...parti.podeste.flat()];
  const us = puncte.map((q) => q[0] * ux + q[1] * uy);
  const ns = puncte.map((q) => q[0] * nx + q[1] * ny);
  return { length: Math.max(...us) - Math.min(...us),
           width: Math.max(...ns) - Math.min(...ns) };
}

/** DOES THE LANDING DEPTH LENGTHEN THE STAIR? Only a U-turn landing that
 *  ends the shape does: a corner is the crossing of two bands and an in-line
 *  landing is the gap between its flights, neither reads `podest_m`. Asked
 *  by the lever that pours a typed total length into the depth. */
export function podestulLungeste(sc: {
  rampe: readonly Rampa[]; latime_m: number; podest_m?: number;
  podeste?: readonly { k: number; contur: readonly Pt[] }[];
}): boolean {
  const la = (podest_m: number) => intinderileScarii({ ...sc, podest_m })?.length ?? 0;
  return la(30) - la(0.001) > 0.001;
}

function colturi(r: Rampa, lat: number): Pt[] {
  const { nx, ny } = axe(r);
  const h = lat / 2;
  return [
    [r.de[0] + nx * h, r.de[1] + ny * h], [r.la[0] + nx * h, r.la[1] + ny * h],
    [r.la[0] - nx * h, r.la[1] - ny * h], [r.de[0] - nx * h, r.de[1] - ny * h],
  ];
}

const dPoly = (p: Pt[]) =>
  p.map((q, i) => `${i ? "L" : "M"} ${q[0].toFixed(3)} ${q[1].toFixed(3)}`)
   .join(" ") + " Z";

/** PODESTUL DINTRE DOUĂ RAMPE — făcut singur, ca în Revit.
 *
 *  DOUĂ CAZURI, și al doilea e tocmai cel al scării în U.
 *
 *  1. RAMPE ÎN UNGHI (scara în L): capătul uneia și începutul celeilalte sunt
 *     două muchii care NU stau pe aceeași dreaptă, deci învelitoarea lor
 *     convexă e chiar colțul de podest care le leagă. Un caz scris pentru
 *     fiecare unghi n-ar fi adus nimic în plus.
 *
 *  2. RAMPE ÎNTOARSE, cu capetele pe aceeași linie (scara în U, cea din video):
 *     aici învelitoarea e DEGENERATĂ — toate cele patru colțuri cad pe aceeași
 *     dreaptă, deci aria e zero și nu s-ar desena nimic. Dar podestul există, și
 *     e chiar locul în care omul se întoarce: trece DINCOLO de linia de
 *     întoarcere, cât o rampă de lată, și ține de la marginea de afară a unei
 *     rampe la a celeilalte. Fără cazul ăsta, o scară în U trasă cu rampele
 *     aliniate (adică exact cum se trage) rămânea fără podest. */
/** CÂT POATE FI DE LUNG UN PODEST, între două rampe în prelungire: 2,20 m.
 *
 *  Cât o rampă lată plus o palmă — peste atât, pe o scară de bloc, nu mai e
 *  odihna dintre două rampe, ci o gaură între două rampe citite greșit. */
const PODEST_MAX = 2.2;

function podest(a: Rampa, b: Rampa, lat: number,
                adanc?: number): Pt[] {
  const ca = colturi(a, lat), cb = colturi(b, lat);
  const A = axe(a);

  // SE ÎNTOARCE DRUMUL? Asta hotărăște, nu aria a ceva.
  //
  // Întâi se încerca învelitoarea convexă a celor două capete și se trecea la
  // dreptunghi doar dacă ieșea prea mică. Pe o scară trasă de mână capetele nu
  // cad NICIODATĂ exact pe aceeași linie — un centimetru de abatere e normal —,
  // iar atunci învelitoarea nu mai e degenerată: e o așchie de un centimetru,
  // destul de mare cât să treacă pragul. Podestul ieșea o LINIE («why is the
  // landing not working when i custom make this», 05.10.2026).
  //
  // Acum se întreabă ce trebuia întrebat de la început: merg rampele una
  // împotriva celeilalte? Dacă da, omul se întoarce, iar podestul stă DINCOLO
  // de capetele amândurora — oricât ar fi ele de nealiniate.
  if (formaScarii([a, b]) === "u") {
    const toate = [ca[1], ca[2], cb[0], cb[3]];
    const pe = toate.map((q) => q[0] * A.nx + q[1] * A.ny);
    const n0 = Math.min(...pe), n1 = Math.max(...pe);
    // linia de întoarcere: dincolo de capătul cel mai depărtat al amândurora,
    // ca podestul să atingă chiar amândouă rampele
    const t0 = Math.max(ca[1][0] * A.ux + ca[1][1] * A.uy,
                        cb[0][0] * A.ux + cb[0][1] * A.uy);
    const t1 = t0 + (adanc && adanc > 0 ? adanc : lat);
    const pct = (t: number, n: number): Pt =>
      [A.ux * t + A.nx * n, A.uy * t + A.ny * n];
    return [pct(t0, n0), pct(t1, n0), pct(t1, n1), pct(t0, n1)];
  }

  // SE COTEȘTE? Atunci podestul e COLȚUL — încrucișarea celor două benzi.
  //
  // Învelitoarea de mai jos e bună cât rampele merg pe aceeași linie: locul
  // dintre capete E podestul. Pe un sfert de tur însă capetele stau în unghi,
  // iar învelitoarea lor taie colțul pe diagonală — adică exact bucata pe care
  // calci când te întorci. Podestul unui colț e pătratul în care intră
  // amândouă benzile de lățime: se taie cele patru laturi ale uneia cu cele
  // patru ale celeilalte, și din punctele alea iese colțul întreg.
  if (formaScarii([a, b]) === "cotita") {
    const linii = (c: Pt[]): [Pt, Pt][] => [[c[0], c[1]], [c[3], c[2]]];
    const taie = (p1: Pt, p2: Pt, p3: Pt, p4: Pt): Pt | null => {
      const d1x = p2[0] - p1[0], d1y = p2[1] - p1[1];
      const d2x = p4[0] - p3[0], d2y = p4[1] - p3[1];
      const det = d1x * d2y - d1y * d2x;
      if (Math.abs(det) < 1e-9) return null;
      const t = ((p3[0] - p1[0]) * d2y - (p3[1] - p1[1]) * d2x) / det;
      return [p1[0] + d1x * t, p1[1] + d1y * t];
    };
    const col: Pt[] = [];
    for (const [a1, a2] of linii(ca)) {
      for (const [b1, b2] of linii(cb)) {
        const q = taie(a1, a2, b1, b2);
        if (q) col.push(q);
      }
    }
    if (col.length >= 3) return inchide([...col, ca[1], ca[2], cb[0], cb[3]]);
  }

  // Rampele merg mai departe în aceeași direcție (scară dreaptă cu odihnă):
  // podestul e chiar locul dintre ele.
  //
  // DAR NU MAI LUNG DECÂT UN PODEST, și asta e o plasă, nu o măsură.
  //
  // Locul dintre două rampe e podest cât timp rampele chiar se leagă. Pe o
  // scară citită greșit din planșă ele pot cădea la capete opuse ale casei
  // scării, iar «locul dintre ele» devine un dreptunghi cât toată camera: se
  // suprapune peste odăile de alături și le fură clicul — «podestul se întinde
  // prea mult și se overlapuiește cu camerele, nu pot să selectez chestii»
  // (arhitectul, 12.10.2026).
  //
  // Un podest de scară are cât lățimea rampei, cel mult vreo doi metri; peste
  // `PODEST_MAX` nu mai e un podest, e o citire greșită. Atunci se desenează
  // doar bucata de lângă rampe (câte o jumătate de podest la fiecare capăt),
  // ca scara să rămână citibilă și să se vadă că între ele lipsește ceva.
  const dus = [ca[1], ca[2], cb[0], cb[3]];
  const peDrum = dus.map((q) => q[0] * A.ux + q[1] * A.uy);
  const lung = Math.max(...peDrum) - Math.min(...peDrum);
  const capul = Math.max(lat, adanc && adanc > 0 ? adanc : 0);
  if (lung > Math.max(PODEST_MAX, capul)) {
    const t0 = Math.min(...peDrum);
    const t1 = Math.max(...peDrum);
    const peLat = dus.map((q) => q[0] * A.nx + q[1] * A.ny);
    const n0 = Math.min(...peLat);
    const n1 = Math.max(...peLat);
    const pct = (t: number, n: number): Pt =>
      [A.ux * t + A.nx * n, A.uy * t + A.ny * n];
    return [pct(t0, n0), pct(t0 + capul / 2, n0),
            pct(t0 + capul / 2, n1), pct(t0, n1),
            pct(t1 - capul / 2, n1), pct(t1, n1),
            pct(t1, n0), pct(t1 - capul / 2, n0)];
  }
  return inchide(dus);
}

/** Învelitoarea convexă a câtorva puncte (Andrew monotone chain). */
function inchide(pts: Pt[]): Pt[] {
  const p = [...pts].sort((u, v) => u[0] - v[0] || u[1] - v[1]);
  if (p.length < 3) return p;
  const cross = (o: Pt, a: Pt, b: Pt) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const jos: Pt[] = [], sus: Pt[] = [];
  for (const q of p) {
    while (jos.length >= 2 && cross(jos[jos.length - 2], jos[jos.length - 1], q) <= 0)
      jos.pop();
    jos.push(q);
  }
  for (let i = p.length - 1; i >= 0; i--) {
    const q = p[i];
    while (sus.length >= 2 && cross(sus[sus.length - 2], sus[sus.length - 1], q) <= 0)
      sus.pop();
    sus.push(q);
  }
  jos.pop(); sus.pop();
  return [...jos, ...sus];
}

/** SCARA TRASĂ, desenată: rampele cu treptele lor, podestele, liniile de
 *  urcare și — la o scară cu podest — semicercul de evacuare.
 *
 *  `pana` oprește desenul la rampa în lucru: cât se trage a doua rampă, prima e
 *  deja scară, iar cea din mână e doar o fantomă. */
export function ScaraDinRampe({ rampe, latime, hNivel, traseu = false,
                               adancPodest, treapta, pas, podeste }: {
  rampe: readonly Rampa[];
  latime: number;
  hNivel: number;
  traseu?: boolean;
  /** Formele scrise ale podestelor, pe indicele lor. V. `partileScarii`. */
  podeste?: readonly (readonly Pt[] | undefined)[];
  /** Adâncimea podestului, când e alta decât lățimea rampei. */
  adancPodest?: number;
  /** ADÂNCIMEA TREPTEI ȘI PASUL, când documentul le scrie.
   *
   *  Desenul le ignora, și atunci planșa arăta altceva decât panoul: pe o
   *  scară căreia i s-a scris adâncimea de 0,32 m, `schimbaScaraParte` îi face
   *  rampa de 7 × 0,32 = 2,24 m, panoul o numără cu 0,32 și spune 8
   *  contratrepte, iar desenul o numără cu 0,28 implicit și desenează 9. Pe o
   *  scară în U pasul ieșea 2,80/18 = 15,56 cm în loc de 2,80/16 = 17,50. */
  treapta?: number;
  pas?: number;
}): ReactNode {
  if (!rampe.length || !(latime > 0)) return null;
  const s = scaraDinRampe(rampe, hNivel, treapta);
  const risc = pas ?? s.pas;
  // ACELEAȘI forme ca cele pe care se apasă (`partileScarii`), ca desenul și
  // mâna să spună același lucru — altfel podestul scris s-ar desena dreptunghi
  // și s-ar apuca de forma lui adevărată.
  const forme = partileScarii(rampe, latime, adancPodest, podeste).podeste;

  return (
    <g>
      {/* PODESTELE ÎNTÂI, sub rampe: la o scară în U rampa se termină chiar pe
          podest, iar un podest desenat peste ar acoperi ultima treaptă. */}
      {forme.map((p, i) => (
        <path key={`p${i}`} d={dPoly(p)} fill={FOND} stroke={LINIE}
              style={{ strokeWidth: g(0.03) }} vectorEffect="non-scaling-stroke" />
      ))}
      {rampe.map((r, i) => (
        <RampaDesen key={`r${i}`} r={r} lat={latime}
                    n={s.perRampa[i] ?? 0} pas={risc} />
      ))}
      {/* DOMUL DE EVACUARE NUMAI UNDE SE ÎNTOARCE DRUMUL. Pe un podest de
          trecere — două rampe care merg în aceeași direcție, cu un podest între
          ele, adică o scară dreaptă cu odihnă — nimeni nu se întoarce, deci
          aria de întoarcere n-are ce căuta acolo. Se desenează doar acolo unde
          rampa următoare chiar merge invers. */}
      {traseu && forme.map((pod, i) =>
        seIntoarce(rampe[i], rampe[i + 1])
          ? <TraseuPodest key={`t${i}`} a={rampe[i]} p={pod} />
          : null)}
    </g>
  );
}

/** Rampa a doua merge invers față de prima? Atunci pe podestul dintre ele
 *  omul se întoarce — și numai atunci. */
function seIntoarce(a: Rampa, b: Rampa): boolean {
  // NU NUMAI LA 180°. Cerea produsul scalar negativ, deci numai o întoarcere
  // completă; pe un sfert de tur el e EXACT 0 și traseul nu primea aria de
  // întoarcere, deși acolo omul chiar se întoarce — cu targa, cu tot.
  return formaScarii([a, b]) !== "dreapta";
}

/** O RAMPĂ: conturul, treptele și linia de urcare cu săgeata.
 *
 *  `n` sunt contratreptele ei; treptele DESENATE sunt cu una mai puține —
 *  ultima urcă pe podest, nu pe o treaptă a rampei. */
function RampaDesen({ r, lat, n, pas }: {
  r: Rampa; lat: number; n: number; pas: number;
}) {
  const { ux, uy, nx, ny, L } = axe(r);
  const c = colturi(r, lat);
  const trepte = Math.max(0, n - 1);
  const dt = trepte > 0 ? L / trepte : L;
  // Tăietura planului: pe la 1,20 m deasupra pardoselii, ca în Revit.
  const taiat = Math.max(1, Math.min(trepte, Math.round(1.2 / (pas || 0.18))));
  const h = lat / 2;
  return (
    <g>
      <path d={dPoly(c)} fill={FOND} stroke={LINIE}
            style={{ strokeWidth: g(0.03) }} vectorEffect="non-scaling-stroke" />
      {Array.from({ length: Math.max(0, trepte - 1) }, (_, i) => {
        const t = dt * (i + 1);
        const x = r.de[0] + ux * t, y = r.de[1] + uy * t;
        return (
          <line key={i} x1={x + nx * h} y1={y + ny * h}
                x2={x - nx * h} y2={y - ny * h} stroke={LINIE}
                strokeDasharray={i + 1 > taiat ? `${g(0.08)} ${g(0.06)}` : undefined}
                style={{ strokeWidth: g(0.018) }} vectorEffect="non-scaling-stroke" />
        );
      })}
      {/* linia de urcare: bulina la primul nas, săgeata la ultimul */}
      <circle cx={r.de[0] + ux * 0.06} cy={r.de[1] + uy * 0.06} r={0.05}
              fill={LINIE} />
      <line x1={r.de[0] + ux * 0.06} y1={r.de[1] + uy * 0.06}
            x2={r.la[0] - ux * 0.04} y2={r.la[1] - uy * 0.04} stroke={LINIE}
            style={{ strokeWidth: g(0.03) }} vectorEffect="non-scaling-stroke" />
      <path d={sageata(r.la, ux, uy, nx, ny, Math.min(0.14, lat * 0.16))}
            fill="none" stroke={LINIE} style={{ strokeWidth: g(0.03) }}
            vectorEffect="non-scaling-stroke" />
    </g>
  );
}

function sageata(p: Pt, ux: number, uy: number, nx: number, ny: number, a: number) {
  const v = 0.22;
  const bx = p[0] - ux * v, by = p[1] - uy * v;
  return `M ${bx + nx * a} ${by + ny * a} L ${p[0]} ${p[1]} `
       + `L ${bx - nx * a} ${by - ny * a}`;
}

/** DOMUL DE EVACUARE, pe podestul dintre două rampe.
 *
 *  SE MĂSOARĂ PE PODEST, nu pe rampă: deschiderea lui ține de la un capăt al
 *  podestului la celălalt, iar înălțimea e chiar adâncimea podestului — deci îl
 *  umple, oricât ar fi el. Măsurat pe capătul rampei, cum era întâi, ieșea mai
 *  îngust decât podestul (podestul ține cât AMÂNDOUĂ rampele, plus ochiul
 *  dintre ele) și nu urca până în fundul lui.
 *
 *  De-aia nu e cerc, ci jumătate de ELIPSĂ: lățimea și adâncimea podestului
 *  sunt două măsuri deosebite, egale doar din întâmplare.
 *
 *  Se calculează în sistemul rampei care intră în podest: pe direcția ei se
 *  măsoară adâncimea, pe normala ei deschiderea. */
function TraseuPodest({ a, p }: { a: Rampa; p: Pt[] }) {
  if (p.length < 3) return null;
  const A = axe(a);
  const pe = p.map((q) => q[0] * A.nx + q[1] * A.ny);     // deschiderea
  const de = p.map((q) => q[0] * A.ux + q[1] * A.uy);     // adâncimea
  const n0 = Math.min(...pe), n1 = Math.max(...pe);
  const t0 = Math.min(...de), t1 = Math.max(...de);
  const rx = (n1 - n0) / 2, ry = t1 - t0;
  if (rx <= 0.01 || ry <= 0.01) return null;
  // SE DESENEAZĂ ÎN SISTEMUL PODESTULUI, punct cu punct, nu ca arc de elipsă
  // rotit: cu `A rx ry rot …` burta ieșea de partea cealaltă — peste rampe, nu
  // în podest —, iar care din cele două o întoarce (`sweep`, semnul rotirii)
  // depinde de încotro merge rampa. Aici direcția e scrisă: `t` crește ÎNSPRE
  // fundul podestului, deci burta nu poate ieși în altă parte.
  const nm = (n0 + n1) / 2;
  const P = (t: number, n: number): Pt =>
    [A.ux * t + A.nx * n, A.uy * t + A.ny * n];
  const N = 28;
  const pct: Pt[] = Array.from({ length: N + 1 }, (_, i) => {
    const th = (Math.PI * i) / N;
    return P(t0 + ry * Math.sin(th), nm - rx * Math.cos(th));
  });
  return (
    <path d={pct.map((q, i) => `${i ? "L" : "M"} ${q[0].toFixed(3)} ${q[1].toFixed(3)}`).join(" ")}
          fill="none" stroke={LINIE} opacity={0.75}
          strokeDasharray={`${g(0.13)} ${g(0.085)}`}
          style={{ strokeWidth: g(0.028) }} vectorEffect="non-scaling-stroke" />
  );
}
